/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / handlers comme U3110 */
/* eslint-disable react-hooks/exhaustive-deps -- effets chargement id */
import React, { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U3104ROLES } from "../U3104";
import U3104AccountingYearBody from "./form/u3104AccountingYear/U3104AccountingYearBody";
import { U3104AccountingYearFormToolBar } from "./form/u3104AccountingYear/U3104AccountingYearFormToolBar";
import U3104AccountingYearDeleteDlg from "./common/U3104AccountingYearDeleteDlg";
import U3104NewAccountingYearDialog from "./common/U3104NewAccountingYearDialog";
import { useU3104Context } from "../U3104Context";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  buildAccountingPeriodsFromFirstPeriodDay,
  type U3104PeriodEndPattern,
  createEmptyU3104FormRow,
  formToSearchRow,
  luHandleAppiError,
  normalizeU3104FromApi,
  type U3104AccountingYearFormRow,
  type U3104AccountingYearGet,
  type U3104AccountingYearSearchRow,
  type U3104AccountingYearWithOrg,
} from "../U3104Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";

function normalizeForForm(row: U3104AccountingYearGet | U3104AccountingYearWithOrg): U3104AccountingYearFormRow {
  return normalizeU3104FromApi(row);
}

function parseDateMs(s: string): number | null {
  const t = String(s ?? "").trim();
  if (!t) return null;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

export interface U3104AccountingYearFormExtendedHandlers extends U2000FormStateHandlersReturn<U3104AccountingYearFormRow> {
  addNewAccountingYear: () => void;
  modifyAccountingYear: () => void;
  deleteAccountingYear: () => Promise<void>;
}

const U3104AccountingYearForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3104Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [newYearWizardComplete, setNewYearWizardComplete] = useState(false);
  const [newYearWizardFromFormOpen, setNewYearWizardFromFormOpen] = useState(false);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith("fr");
  const listTitle = t("title", { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const validateForm = (row: U3104AccountingYearFormRow) => {
    const result: Record<string, string> = {};
    const year = row.yearCode?.trim() ?? "";
    if (!year) {
      result["yearCode"] = t("errYearCodeRequired", {
        ns: NS,
        defaultValue: isFr ? "Le code année est obligatoire." : "Year code is required.",
      });
    } else if (year.length > 50) {
      result["yearCode"] = t("errYearCodeLength", {
        ns: NS,
        defaultValue: isFr ? "Le code année est trop long." : "Year code is too long.",
      });
    }

    const periods = row.accountingPeriods ?? [];
    if (periods.length !== 12) {
      result["accountingPeriods"] = t("errPeriodsCount", {
        ns: NS,
        defaultValue: isFr ? "Exactement 12 périodes sont requises." : "Exactly 12 periods are required.",
      });
    }

    const nums = periods.map((p) => p.periodNumber);
    const uniq = new Set(nums);
    if (uniq.size !== nums.length || nums.some((n) => n < 1 || n > 12 || !Number.isInteger(n))) {
      result["accountingPeriods"] = t("errPeriodNumbersInvalid", {
        ns: NS,
        defaultValue: isFr ? "Les numéros de période doivent être 1 à 12, tous distincts." : "Period numbers must be 1 to 12, all distinct.",
      });
    }

    periods.forEach((p, i) => {
      const base = `accountingPeriods.${i}`;
      const startMs = parseDateMs(p.startAt);
      const endMs = parseDateMs(p.endAt);
      if (startMs === null) {
        result[`${base}.startAt`] = t("errPeriodDateInvalid", { ns: NS });
      }
      if (endMs === null) {
        result[`${base}.endAt`] = t("errPeriodDateInvalid", { ns: NS });
      }
      if (startMs !== null && endMs !== null && startMs >= endMs) {
        result[`${base}.endAt`] = t("errPeriodEndAfterStart", { ns: NS });
      }
    });

    return result;
  };

  const u3104BL: U2000FormSBusinessHandlers<U3104AccountingYearFormRow> = {
    newRecord: () => createEmptyU3104FormRow(),

    validateForm,

    saveForme: async (row: U3104AccountingYearFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3104FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },

    cancelForm: () => {
      const old = u3104FormExtended.getOldData() as U3104AccountingYearFormRow | null;
      if (old) {
        u3104FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === "/U3104") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    onLeaveForm: () => {
      navigate("/U3104");
    },
  };

  const u3104FormHandlers = useU2000FormStateHandlers<U3104AccountingYearFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3104BL,
  });

  const u3104FormExtended: U3104AccountingYearFormExtendedHandlers = {
    ...u3104FormHandlers,
    addNewAccountingYear: () => {
      setNewYearWizardFromFormOpen(true);
    },
    modifyAccountingYear: () => {
      u3104FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteAccountingYear: async () => {
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3104AccountingYearSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3104AccountingYearSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3104AccountingYearFormRow>({
    doGetFn: (rowId: string) => {
      return doGetData(u2000AxiosAuthenticated, rowId);
    },
    onSuccess: (row: U3104AccountingYearFormRow) => {
      const normalized = normalizeForForm(row as unknown as U3104AccountingYearGet);
      gridStateHandlers.setDataById(normalized.id, formToSearchRow(normalized));
      u3104FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const crumbLabel = `${normalized.yearCode}`;
      setLogicalUnitBreadcrumb([
        { href: "/U3104", label: listTitle },
        { label: crumbLabel },
      ]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3104FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3104AccountingYearFormRow>({
    doSaveFn: (row: U3104AccountingYearFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3104AccountingYearFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeForForm(row as unknown as U3104AccountingYearWithOrg);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), formToSearchRow(normalized)]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(row.id, formToSearchRow(normalized));
      }
      u3104FormExtended.afterSaveData(normalized);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3104") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3104FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3104FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3104FormExtended.getData()],
    doDeleteFn: async (rowId, row) => {
      const r = row as U3104AccountingYearFormRow;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(r));
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        toast.success(t("success0003", { key: (del as U3104AccountingYearFormRow).yearCode, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3104", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    setNewYearWizardFromFormOpen(false);
    if (id === "new") {
      setNewYearWizardComplete(false);
    } else {
      setNewYearWizardComplete(true);
      handleGet.getData(id);
    }
  }, [id]);

  const applyNewYearWizard = (yearCode: string, firstPeriodDay: string, periodEndPattern: U3104PeriodEndPattern) => {
    const accountingPeriods = buildAccountingPeriodsFromFirstPeriodDay(firstPeriodDay, { periodEndPattern });
    u3104FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    u3104FormExtended.setData({
      id: "",
      yearCode,
      version: 0,
      accountingPeriods,
    });
    setNewYearWizardComplete(true);
    setNewYearWizardFromFormOpen(false);
    setLogicalUnitBreadcrumb([
      { href: "/U3104", label: listTitle },
      { label: t("breadcrumbNew", { ns: NS }) },
    ]);
  };

  const handleNewYearWizardCancel = () => {
    setNewYearWizardFromFormOpen(false);
    if (id === "new") {
      navigate("/U3104", { replace: true });
    }
  };

  if (id === "new" && !newYearWizardComplete) {
    return (
      <U3104NewAccountingYearDialog
        isOpen={true}
        onApply={applyNewYearWizard}
        onCancel={handleNewYearWizardCancel}
        t={t}
        NS={NS}
      />
    );
  }

  if (u3104FormExtended.getData() === null) {
    return null;
  }

  const formTabs = [
    hasRole(U3104ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={u3104FormExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U3104AccountingYearAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      {newYearWizardFromFormOpen && (
        <U3104NewAccountingYearDialog
          isOpen={true}
          onApply={applyNewYearWizard}
          onCancel={handleNewYearWizardCancel}
          t={t}
          NS={NS}
        />
      )}

      <U3104AccountingYearDeleteDlg handleDelete={handleDelete as any} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3104AccountingYearFormToolBar formStateHandlers={u3104FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3104FormExtended as any}
      >
        <U3104AccountingYearBody formStateHandlers={u3104FormExtended as any} t={t} />

        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={formTabs[0]?.id}
            activeTab={activeTab ?? formTabs[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3104AccountingYearForm;
